import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["aria-label", "accessibilityLabel", "backgroundColorToken", "backgroundColorTokenArgs", "borderColorToken", "borderColorTokenArgs", "children", "id", "preset", "role", "size", "source", "testID", "textColorToken", "textColorTokenArgs", "UNSAFE_containerProps", "UNSAFE_textProps"];
import React from 'react';
import { View } from '@bubble-ui/primitive-view';
import { Text } from '@bubble-ui/primitive-text';
import { DesignContextForwardProvider, useDesignContext } from '@klarna/designtokens';
import { SpacerHorizontal } from '@bubble-ui/atom-spacer-horizontal';
import { ActionIconSuccess } from '@bubble-ui/icon-action-success';
import { ActionIconWarning } from '@bubble-ui/icon-action-warning';
import { ActionIconError } from '@bubble-ui/icon-action-error';
import { Image } from '@bubble-ui/primitive-image';
import { getDisplayName } from '@bubble-ui/util-get-display-name';
import { isNative } from '@bubble-ui/util-platform';
import { usePropsSpreadingDeprecationWarning } from '@bubble-ui/hook-use-props-spreading-deprecation-warning';
import { defaultComponentTokens } from './defaultComponentTokens';
import { getDefaultStyleSheet } from './getDefaultStyleSheet';
import { getIds } from './getIds';
var Badge = _ref => {
  var {
      'aria-label': ariaLabel,
      accessibilityLabel: ariaLabelDeprecated,
      backgroundColorToken,
      backgroundColorTokenArgs,
      borderColorToken,
      borderColorTokenArgs,
      children,
      id,
      preset,
      role,
      size = 'regular',
      source,
      testID,
      textColorToken,
      textColorTokenArgs,
      UNSAFE_containerProps,
      UNSAFE_textProps
      /**
       * @deprecated props spreading is outdated and will be removed with v7.0.0
       */
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  var ids = getIds(id);
  var designContext = useDesignContext(defaultComponentTokens);
  usePropsSpreadingDeprecationWarning({
    componentName: 'Badge',
    props,
    useInstead: "UNSAFE_containerProps' or 'UNSAFE_textProps",
    removalDate: 'v7.0.0'
  });
  var isStatusPreset = preset && ['error', 'warning', 'success'].includes(preset);
  var {
    rootStyle,
    innerViewStyle,
    imageStyle,
    contentStyle,
    textStyle,
    actionIconWrapperStyle,
    actionIconProps
  } = getDefaultStyleSheet({
    backgroundColorToken,
    backgroundColorTokenArgs,
    borderColorToken,
    borderColorTokenArgs,
    designContext,
    isCircle: source !== undefined || !isStatusPreset && (typeof children === 'string' && children.length === 1 || typeof children === 'number' && children >= 0 && children <= 9),
    preset,
    size,
    textColorToken,
    textColorTokenArgs
  });
  var filteredChildren = React.Children.toArray(children).filter(child => /*#__PURE__*/React.isValidElement(child) || Boolean(child));
  var childrenCount = filteredChildren.length;
  return /*#__PURE__*/React.createElement(View, _extends({
    id: ids === null || ids === void 0 ? void 0 : ids.root,
    testID: testID
  }, UNSAFE_containerProps, {
    style: rootStyle
  }, props), /*#__PURE__*/React.createElement(View, _extends({
    id: ids === null || ids === void 0 ? void 0 : ids.innerView,
    style: innerViewStyle
  }, props), source ? /*#__PURE__*/React.createElement(Image, _extends({
    source: source,
    style: imageStyle
  }, isNative ? {
    accessible: Boolean(ariaLabel || ariaLabelDeprecated),
    'aria-label': ariaLabel || ariaLabelDeprecated,
    accessibilityRole: role ? role : ariaLabel || ariaLabelDeprecated ? 'image' : undefined
  } : {
    'aria-hidden': !(ariaLabel || ariaLabelDeprecated),
    alt: ariaLabel || ariaLabelDeprecated,
    role
  })) : /*#__PURE__*/React.createElement(View, {
    style: contentStyle,
    accessible: Boolean(ariaLabel || ariaLabelDeprecated || role) || undefined,
    "aria-label": ariaLabel || ariaLabelDeprecated,
    accessibilityRole: role,
    role: role
  }, isStatusPreset && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DesignContextForwardProvider, {
    context: designContext
  }, /*#__PURE__*/React.createElement(View, {
    style: actionIconWrapperStyle
  }, preset === 'error' ? /*#__PURE__*/React.createElement(ActionIconError, actionIconProps) : preset === 'warning' ? /*#__PURE__*/React.createElement(ActionIconWarning, actionIconProps) : /*#__PURE__*/React.createElement(ActionIconSuccess, actionIconProps))), /*#__PURE__*/React.createElement(SpacerHorizontal, {
    spaceToken: "space/50"
  })), filteredChildren.map((child, i) => {
    var _getDisplayName;
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: i
    }, /*#__PURE__*/React.isValidElement(child) ? (_getDisplayName = getDisplayName(child)) !== null && _getDisplayName !== void 0 && _getDisplayName.startsWith('ActionIcon') ? /*#__PURE__*/React.createElement(DesignContextForwardProvider, {
      context: designContext
    }, /*#__PURE__*/React.createElement(View, {
      style: actionIconWrapperStyle
    }, /*#__PURE__*/React.cloneElement(child, actionIconProps))) : child : /*#__PURE__*/React.createElement(Text, _extends({
      id: ids === null || ids === void 0 ? void 0 : ids.paragraph
    }, UNSAFE_textProps, {
      style: textStyle
    }, props), child), i < childrenCount - 1 && /*#__PURE__*/React.createElement(SpacerHorizontal, {
      spaceToken: "space/50"
    }));
  }))));
};
Badge.displayName = 'Badge';
export { Badge };
//# sourceMappingURL=index.js.map